<template>
	<div class="buybox-type-switcher">
		<div
			class="buybox-type-switcher__button buybox-type-switcher__button--left"
			:class="{ active: buyboxView !== 'grid' }"
			@click="handleListClicked"
		>
			<span class="buybox-type-switcher__button__text">{{ t('WEBAPP_LIST') }}</span>
			<FontAwesomeIcon :icon="faList" />

			<!-- Tooltip -->
			<!-- <NoSSR>
				<div v-if="showTooltip && buyboxView !== 'list'" class="tooltip">
					<div class="tooltip__content">
						<FontAwesomeIcon class="tooltip__content__info" :icon="faInfoCircle" />
						<span class="tooltip__content__text">{{ $t('WEBAPP_SWITCH_TO_LIST_VIEW') }}</span>
						<FontAwesomeIcon class="tooltip__content__close" :icon="faTimes" @click.stop="close" />
					</div>
					<div class="tooltip__caret"></div>
				</div>
			</NoSSR> -->
		</div>
		<div
			class="buybox-type-switcher__button buybox-type-switcher__button--right"
			:class="{ active: buyboxView === 'grid' }"
			@click="handleGridClicked"
		>
			<span class="buybox-type-switcher__button__text">{{ t('WEBAPP_GRID') }}</span>
			<FontAwesomeIcon :icon="faTableCells" />

			<!-- Tooltip -->
			<!-- <NoSSR>
				<div v-if="showTooltip && buyboxView !== 'grid'" class="tooltip">
					<div class="tooltip__content">
						<FontAwesomeIcon class="tooltip__content__info" :icon="faInfoCircle" />
						<span class="tooltip__content__text">{{ t('WEBAPP_SWITCH_TO_GRID_VIEW') }}</span>
						<FontAwesomeIcon class="tooltip__content__close" :icon="faTimes" @click.stop="close" />
					</div>
					<div class="tooltip__caret"></div>
				</div>
			</NoSSR> -->
		</div>
	</div>
</template>

<script setup lang="ts">
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { computed } from 'vue';
import { faTableCells, faList } from '@fortawesome/free-solid-svg-icons';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useBuyboxView } from '@/features/buybox/composables/useBuyboxView';
import { useMounted } from '@vueuse/core';

const props = defineProps<{
	additionalContexts: SnowplowContext[];
	buyBoxType?: NewBuyBoxView;
	title: TitleDetail;
}>();

const emit = defineEmits<{
	(e: 'buyBoxTypeSwitched', payload: { buyBoxType: NewBuyBoxView }): void;
}>();

const userStore = useUserStore();
const {
	setNewBuyboxTooltipAcknowledged,
	setNewBuyboxView,
	setBuyboxExpView,
	setBuyboxViewExpTooltipAcknowledged,
	setBuyboxFilterTypeOptimistic,
} = userStore;
const {
	newBuyboxTooltipAcknowledgedGetter,
	newBuyboxView,
	isUserFirstVisit,
	isLoggedIn,
	loggedInNewBuyboxView,

	// OLD BUYBOX EXP
	buyboxExpView,
	buyboxViewExpTooltipAcknowledged,
} = storeToRefs(userStore);

// OLD BUYBOX EXP
const { isGridBuyboxDefault } = useBuyboxView({ objectType: () => props.title.objectType });
// OLD BUYBOX EXP

const { t } = useI18n();

const isMounted = useMounted();

const buyboxView = computed(() => {
	if (!!props.buyBoxType) {
		return props.buyBoxType;
	}

	// OLD BUYBOX EXP
	if (isGridBuyboxDefault.value) return buyboxExpView.value;
	// OLD BUYBOX EXP

	if (isLoggedIn.value && (props.title.__typename === 'Season' || props.title.__typename === 'Show')) {
		return loggedInNewBuyboxView.value;
	}

	return newBuyboxView.value;
});

const showTooltip = computed(() => {
	if (!isMounted.value) return false;
	// OLD BUYBOX EXP
	if (isGridBuyboxDefault.value) {
		return !buyboxViewExpTooltipAcknowledged.value;
	}

	// OLD BUYBOX EXP
	if (isLoggedIn.value) {
		return !newBuyboxTooltipAcknowledgedGetter.value;
	}
	return !newBuyboxTooltipAcknowledgedGetter.value && isUserFirstVisit.value === false;
});

const handleListClicked = () => {
	if (!!props.buyBoxType) {
		emit('buyBoxTypeSwitched', { buyBoxType: NewBuyBoxView.LIST });
	} else {
		handleSetBuyboxView(NewBuyBoxView.LIST);
		handleSetTooltipAcknowledged();
	}
	trackToggleEvent(NewBuyBoxView.LIST);
	setBuyboxFilterTypeOptimistic(null);
};

const handleGridClicked = () => {
	if (!!props.buyBoxType) {
		emit('buyBoxTypeSwitched', { buyBoxType: NewBuyBoxView.GRID });
	} else {
		handleSetBuyboxView(NewBuyBoxView.GRID);
		handleSetTooltipAcknowledged();
	}
	trackToggleEvent(NewBuyBoxView.GRID);
	setBuyboxFilterTypeOptimistic(null);
};

function trackToggleEvent(label: NewBuyBoxView) {
	TrackingHelper.trackEvent('userinteraction', { action: 'buybox_view_clicked', label }, props.additionalContexts);
}

function close() {
	TrackingHelper.trackEvent('userinteraction', { action: 'buybox_switch_acknowledged' }, props.additionalContexts);
	handleSetTooltipAcknowledged();
}

// OLD BUYBOX EXP
function handleSetBuyboxView(viewType: NewBuyBoxView) {
	if (isGridBuyboxDefault.value) {
		setBuyboxExpView(viewType);
	} else {
		setNewBuyboxView(viewType);
	}
}

function handleSetTooltipAcknowledged() {
	if (isGridBuyboxDefault.value) {
		setBuyboxViewExpTooltipAcknowledged();
	} else {
		setNewBuyboxTooltipAcknowledged();
	}
}
// OLD BUYBOX EXP
</script>

<style lang="scss" scoped>
@import '~@/styles/rtl-support-mixins.scss';

.buybox-type-switcher {
	display: flex;
	justify-content: flex-end;
	align-items: center;

	@include rtl() {
		justify-content: flex-start;
		flex-direction: row-reverse;
	}

	&__button {
		background-color: #111924;
		color: #c6c8cd;
		height: 32px;
		min-width: 72px;
		width: fit-content;
		padding: 8px;

		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;

		font-size: 16px;
		font-weight: 400;

		@media (max-width: 767px) {
			min-width: 40px;
			width: 40px;
		}

		&:hover {
			cursor: pointer;
		}

		&--left {
			border-top-left-radius: 500px;
			border-bottom-left-radius: 500px;

			position: relative;

			.tooltip {
				right: -60px;

				&__caret {
					margin-right: 95px;
					@media (max-width: 767px) {
						margin-right: 75px;
					}
				}
			}
		}

		&--right {
			border-top-right-radius: 500px;
			border-bottom-right-radius: 500px;

			position: relative;
		}

		&__text {
			@media (max-width: 767px) {
				display: none;
			}
		}
	}
}

.active {
	background-color: #222c38;
}

.tooltip {
	cursor: default;
	position: absolute;
	top: -55px;
	right: -85px;
	z-index: 10;

	&__content {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 8px 16px;
		background-color: #222c38;
		border-radius: 8px;

		&__info {
			color: #fbc500;
		}

		&__text {
			min-width: fit-content;
			overflow: hidden;
			white-space: nowrap;
		}

		&__close {
			cursor: pointer;
		}
	}

	&__caret {
		width: 0;
		height: 0;
		border-left: 10px solid transparent;
		border-right: 10px solid transparent;
		border-top: 10px solid #222c38;
		margin: 0 auto;

		@media (max-width: 767px) {
			margin-right: 95px;
		}
	}
}
</style>
